<template>
    <div>
      <SettingHeaderTab />
  
      <div class="upload-page">
  
        <!-- Page Header -->
        <div class="page-header">
          <div class="page-title-wrap">
            <router-link :to="$url('/all-customers')" class="back-link">
              <font-awesome-icon icon="angle-left" /> Back
            </router-link>
            <div>
              <h5 class="page-title">Bulk Customer Upload</h5>
              <p class="page-sub">Import multiple customers at once using a spreadsheet</p>
            </div>
          </div>
        </div>
  
        <div class="upload-grid">
  
          <!-- LEFT: Upload card -->
          <div class="upload-card">
  
            <!-- Step 1 -->
            <div class="step-block">
              <div class="step-num">1</div>
              <div class="step-body">
                <div class="step-title">Download the template</div>
                <div class="step-sub">Fill in your customer data using the correct format</div>
                <button
                  class="btn-download"
                  :disabled="downloading"
                  @click="downloadFile('/settings/customer/download/upload', 'Customer_Upload_Template.xlsx')"
                >
                  <font-awesome-icon :icon="downloading ? 'spinner' : 'download'" :spin="downloading" />
                  {{ downloading ? 'Downloading...' : 'Download Template' }}
                </button>
              </div>
            </div>
  
            <div class="step-divider"></div>
  
            <!-- Step 2 -->
            <div class="step-block">
              <div class="step-num">2</div>
              <div class="step-body">
                <div class="step-title">Upload your file</div>
                <div class="step-sub">Select your completed spreadsheet (.xlsx or .csv)</div>
  
                <!-- Dropzone -->
                <label
                  class="dropzone"
                  :class="{ 'dropzone--has-file': import_file_name, 'dropzone--drag': isDragging }"
                  @dragover.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @drop.prevent="onDrop"
                >
                  <div class="dropzone-empty">
                    <div class="dropzone-icon">
                      <font-awesome-icon
                        :icon="import_file_name ? 'check-circle' : 'file-excel'"
                        :class="{ 'icon--ready': import_file_name }"
                      />
                    </div>
                    <p class="dropzone-text" v-if="!import_file_name">Drag your file here or <span class="dropzone-link">browse</span></p>
                    <p class="dropzone-text dropzone-text--ready" v-else>File selected — click to change</p>
                    <p class="dropzone-hint">.xlsx or .csv only</p>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    class="dropzone-input"
                    @change="onFileChange"
                  />
                </label>

                <!-- Selected file card (outside the label so the × button works) -->
                <div v-if="import_file_name" class="file-card">
                  <div class="file-card-icon">
                    <font-awesome-icon :icon="import_file_name.endsWith('.csv') ? 'file-csv' : 'file-excel'" />
                  </div>
                  <div class="file-card-body">
                    <div class="file-card-name">{{ import_file_name }}</div>
                    <div class="file-card-meta">{{ import_file_size }} &middot; Ready to upload</div>
                  </div>
                  <button type="button" class="file-card-remove" @click="clearFile" title="Remove file">
                    <font-awesome-icon icon="times" />
                  </button>
                </div>

                <!-- Validation errors -->
                <div v-if="flatErrors.length" class="error-box">
                  <div class="error-box-title">
                    <font-awesome-icon icon="exclamation-circle" class="me-1" /> Errors found in your file:
                  </div>
                  <ul class="error-list">
                    <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
                  </ul>
                </div>
  
                <!-- Upload button -->
                <button
                  class="btn-upload"
                  :disabled="!import_file || loading"
                  @click="importCustomers"
                >
                  <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                  <font-awesome-icon v-else icon="upload" class="me-1" />
                  {{ loading ? 'Uploading...' : 'Upload Customers' }}
                </button>
  
              </div>
            </div>
  
          </div>
  
          <!-- RIGHT: Instructions -->
          <div class="instructions-card">
            <div class="instructions-header">
              <font-awesome-icon icon="info-circle" class="instructions-icon" />
              <span>Instructions</span>
            </div>
  
            <p class="instructions-intro">
              Follow these steps to ensure your import is successful.
              <a href="#" class="sample-link" @click.prevent="downloadFile('/settings/customer/download/sample', 'Customer_Sample_Template.xlsx')">
                Download a sample file
              </a> to see an example.
            </p>
  
            <div class="instructions-list">
              <div class="instruction-item" v-for="item in instructions" :key="item.step">
                <div class="instruction-num">{{ item.step }}</div>
                <div class="instruction-text">
                  <span class="instruction-label">{{ item.label }}</span>
                  {{ item.desc }}
                </div>
              </div>
            </div>
          </div>
  
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import SettingHeaderTab from './components/settingheadertab.vue';
  import { useReusable } from '../../../lib/reusable';
  
  export default {
    name: 'BulkCustomerUpload',
    components: { SettingHeaderTab },
  
    setup() {
      const { downloading, downloadFile } = useReusable();
      const router = useRouter();
      const toast  = useToast();
  
      const import_file      = ref(null);
      const import_file_name = ref('');
      const import_file_size = ref('');
      const loading          = ref(false);
      const isDragging       = ref(false);
      const validationErrors = ref([]);

      const formatSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
      };
  
      const flatErrors = computed(() => {
        const e = validationErrors.value;
        if (!e || !e.length) return [];
        if (Array.isArray(e[0])) return e.map(err => err[0]);
        return Object.values(e).flat();
      });
  
      const instructions = [
        { step: 1, label: 'Download the template', desc: 'Use the Download Template button above to get the correct spreadsheet format.' },
        { step: 2, label: 'Customer Name:', desc: 'The customer\'s full name. This field is required.' },
        { step: 3, label: 'Phone Number:', desc: 'The customer\'s phone number. Ensure the column format is set to "TEXT" in Excel. Optional.' },
        { step: 4, label: 'Email:', desc: 'The customer\'s email address. Optional.' },
        { step: 5, label: 'Address:', desc: 'The customer\'s street address. Optional.' },
        { step: 6, label: 'Country:', desc: 'The country where the customer is located. Optional.' },
        { step: 7, label: 'State:', desc: 'The state based on the country above. Optional.' },
        { step: 8, label: 'City:', desc: 'The city based on the country and state above. Optional.' },
      ];
  
      const setFile = (file) => {
        if (!file) return;
        import_file.value      = file;
        import_file_name.value = file.name;
        import_file_size.value = formatSize(file.size);
        validationErrors.value = [];
      };

      const clearFile = () => {
        import_file.value      = null;
        import_file_name.value = '';
        import_file_size.value = '';
        validationErrors.value = [];
      };
  
      const onFileChange = (e) => setFile(e.target.files[0]);
      const onDrop       = (e) => { isDragging.value = false; setFile(e.dataTransfer.files[0]); };
  
      const importCustomers = async () => {
        if (!import_file.value) return;
        loading.value = true;
        validationErrors.value = [];
  
        const formData = new FormData();
        formData.append('import_file', import_file.value);
  
        try {
          await axios.post('/settings/customer/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Customers imported successfully.');
          router.push('/all-customers');
        } catch (err) {
          if (err?.message === 'Network Error') {
            import_file.value      = null;
            import_file_name.value = '';
            toast.error('Network error. Please re-select your file and try again.');
            return;
          }
          const status = err.response?.status;
          if (status === 422) {
            validationErrors.value = err.response.data.errors ?? [];
            toast.warning(err.response.data.message);
          } else if (status === 400 || status === 403) {
            toast.error(err.response.data.message);
          } else {
            toast.error('An error occurred. Please try again.');
          }
        } finally {
          loading.value = false;
        }
      };
  
      return {
        downloading, downloadFile,
        import_file, import_file_name, import_file_size, isDragging,
        loading, validationErrors, flatErrors,
        instructions,
        onFileChange, onDrop, importCustomers, clearFile,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .upload-page { display:flex; flex-direction:column; gap:20px; }
  
  /* ── Header ──────────────────────────────────────────────────────────── */
  .page-header     { display:flex; align-items:flex-start; gap:14px; }
  .page-title-wrap { display:flex; align-items:flex-start; gap:14px; }
  .page-title      { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .back-link { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:#7a90b0; padding:6px 10px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; text-decoration:none; transition:all .15s; white-space:nowrap; align-self:flex-start; margin-top:2px; }
  .back-link:hover { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  
  /* ── Grid ────────────────────────────────────────────────────────────── */
  .upload-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; align-items:start; }
  
  /* ── Upload card ─────────────────────────────────────────────────────── */
  .upload-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── Steps ───────────────────────────────────────────────────────────── */
  .step-block   { display:flex; gap:16px; padding:24px; }
  .step-divider { border:none; border-top:1px solid #f0f4f8; }
  .step-num     { width:28px; height:28px; border-radius:50%; background:#004AAD; color:#fff; font-size:13px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:2px; }
  .step-body    { flex:1; display:flex; flex-direction:column; gap:10px; }
  .step-title   { font-size:14px; font-weight:700; color:#1a2b45; }
  .step-sub     { font-size:13px; color:#7a90b0; margin-top:-4px; }
  
  /* ── Download button ─────────────────────────────────────────────────── */
  .btn-download { display:inline-flex; align-items:center; justify-content:center; gap:8px; background:#f4f7fb; color:#004AAD; border:1px solid #c5d6f5; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:all .15s; width:100%; }
  .btn-download:hover:not(:disabled) { background:#e8f0fb; }
  .btn-download:disabled { opacity:.55; cursor:not-allowed; }
  
  /* ── Dropzone ────────────────────────────────────────────────────────── */
  .dropzone { display:block; border:2px dashed #dde5f0; border-radius:10px; padding:32px 20px; text-align:center; cursor:pointer; transition:all .2s; background:#fafcff; position:relative; }
  .dropzone:hover, .dropzone--drag { border-color:#004AAD; background:#f0f6ff; }
  .dropzone--has-file { border-style:solid; border-color:#16a34a; background:#f0fdf4; }
  .dropzone-input { position:absolute; inset:0; width:100%; height:100%; opacity:0; cursor:pointer; }
  
  .dropzone-empty   { display:flex; flex-direction:column; align-items:center; gap:8px; }
  .dropzone-icon    { font-size:36px; color:#7a90b0; }
  .dropzone-text    { font-size:14px; font-weight:600; color:#1a2b45; margin:0; }
  .dropzone-link    { color:#004AAD; }
  .dropzone-hint    { font-size:12px; color:#7a90b0; margin:0; }
  
  .dropzone-file      { display:flex; align-items:center; gap:12px; text-align:left; }
  .dropzone-file-icon { width:36px; height:36px; border-radius:8px; background:#f0fdf4; color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .dropzone-file-name { font-size:13px; font-weight:600; color:#1a2b45; word-break:break-all; }
  .dropzone-file-hint { font-size:11px; color:#7a90b0; margin-top:2px; }
  .dropzone-check     { font-size:18px; color:#16a34a; flex-shrink:0; margin-left:auto; }
  .icon--ready        { color:#16a34a; }
  .dropzone-text--ready { color:#16a34a; font-weight:600; }

  /* ── Selected file card ──────────────────────────────────────────────── */
  .file-card        { display:flex; align-items:center; gap:12px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:10px; padding:12px 14px; margin-top:10px; }
  .file-card-icon   { width:36px; height:36px; border-radius:8px; background:#dcfce7; color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .file-card-body   { flex:1; min-width:0; }
  .file-card-name   { font-size:13px; font-weight:600; color:#1a2b45; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .file-card-meta   { font-size:11.5px; color:#16a34a; margin-top:2px; }
  .file-card-remove { width:28px; height:28px; border-radius:6px; border:1px solid #bbf7d0; background:#fff; color:#7a90b0; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; flex-shrink:0; transition:all .15s; }
  .file-card-remove:hover { background:#fff5f5; border-color:#fecaca; color:#e03535; }

  /* ── Error box ───────────────────────────────────────────────────────── */
  .error-box       { background:#fff5f5; border-left:4px solid #e03535; border-radius:0 8px 8px 0; padding:12px 14px; }
  .error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:6px; display:flex; align-items:center; }
  .error-list      { margin:0; padding-left:18px; }
  .error-list li   { font-size:12px; color:#c02828; margin-bottom:3px; }
  .me-1 { margin-right:4px; }
  
  /* ── Upload button ───────────────────────────────────────────────────── */
  .btn-upload { display:flex; align-items:center; justify-content:center; gap:8px; width:100%; background:#004AAD; color:#fff; font-size:14px; font-weight:600; padding:12px 0; border-radius:9px; border:none; cursor:pointer; transition:background .15s; }
  .btn-upload:hover:not(:disabled) { background:#003b8e; }
  .btn-upload:disabled { opacity:.55; cursor:not-allowed; }
  
  /* ── Instructions card ───────────────────────────────────────────────── */
  .instructions-card   { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .instructions-header { display:flex; align-items:center; gap:10px; font-size:15px; font-weight:700; color:#1a2b45; margin-bottom:12px; }
  .instructions-icon   { color:#004AAD; font-size:16px; }
  .instructions-intro  { font-size:13px; color:#7a90b0; margin-bottom:20px; line-height:1.6; }
  .sample-link         { color:#004AAD; font-weight:600; text-decoration:none; }
  .sample-link:hover   { text-decoration:underline; }
  .instructions-list   { display:flex; flex-direction:column; gap:12px; }
  .instruction-item    { display:flex; align-items:flex-start; gap:12px; }
  .instruction-num     { width:22px; height:22px; border-radius:50%; background:#f4f7fb; color:#004AAD; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:1px; }
  .instruction-text    { font-size:13px; color:#4a5e78; line-height:1.5; }
  .instruction-label   { font-weight:700; color:#1a2b45; margin-right:4px; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:1024px) { .upload-grid { grid-template-columns:1fr; } }
  @media (max-width:768px)  { .page-title-wrap { flex-direction:column; align-items:stretch; } }
  </style>